import { COLLECTION_STATES, PER_OPTIONS, collectionOf, eventIncome, eventProfit, lineTotal, linesTotal, needsQuote } from "../lib/calc.js";
import { formatCurrency, parseAmount, toLocalISODate } from "../lib/format.js";
import { escapeHtml, formatAmountsIn, wireAmountInputs } from "../lib/ui.js";

// El formulario tiene dos mitades:
//
//   operativa   → qué pasó: producto, paquete, fecha, cantidades, campos
//                 propios del producto. La ve y la completa cualquier rol.
//   financiera  → las líneas de costo y el ingreso. SÓLO admin: ni se dibuja
//                 para un empleado, y aunque se dibujara, RLS no lo dejaría
//                 escribir en event_finance.
//
// Que el empleado no vea plata no depende de este archivo — lo garantiza la
// base. Acá simplemente no se pide lo que no corresponde.
//
// Las líneas de costo NO son fijas: salen del paquete. Un pack donde el club
// cobra un monto fijo y el buffet va por chico tiene esas dos líneas y nada
// más, en vez de cinco campos de los cuales tres quedan en cero.

const OPERATIONAL_NUMBERS = [
  ["children_count", "Cantidad de chicos", "1"],
  ["teachers_count", "Cantidad de profesores", "1"],
  ["souvenirs_count", "Cantidad de souvenirs", "1"],
];

/** Debajo de este margen, el formulario avisa antes de guardar. */
export const LOW_MARGIN_RATIO = 0.15;

const numberField = ([name, label, step], source) =>
  step === "any"
    ? `<div class="field">
        <label for="f-${name}">${label}</label>
        <input type="text" inputmode="decimal" id="f-${name}" name="${name}" data-amount
               value="${source?.[name] ?? 0}" />
      </div>`
    : `<div class="field">
        <label for="f-${name}">${label}</label>
        <input type="number" step="${step}" min="0" id="f-${name}" name="${name}"
               inputmode="numeric" value="${source?.[name] ?? 0}" />
      </div>`;

/**
 * Una fila de línea, de costo o de precio. Misma forma; cambia el extra:
 *   costo  → "a presupuestar": el monto lo cotizan por evento (club Belgrano)
 *   precio → "a partir de": desde qué cantidad se cobra (chico extra desde 26)
 */
export function costLineRowHtml(line = { concept: "", amount: 0, per: "fijo" }, kind = "cost", where = "event") {
  const isPrice = kind === "price";
  const quote = Boolean(line.quote);
  const pending = quote && !(Number(line.amount) > 0);
  return `
    <div class="cost-line-row${pending ? " needs-quote" : ""}" data-cost-line data-kind="${kind}"${quote ? ' data-quote="1"' : ""}>
      <div class="field">
        <label>Concepto</label>
        <input type="text" class="line-concept" placeholder="${isPrice ? "Paquete (hasta 25 chicos)" : "Buffet"}" value="${escapeHtml(line.concept ?? "")}" />
      </div>
      <div class="field">
        <label>Monto</label>
        <input type="text" inputmode="decimal" class="line-amount" data-amount
               value="${pending || line.amount === "" || line.amount == null ? "" : line.amount}"
               ${pending ? 'placeholder="a presupuestar"' : ""} />
      </div>
      <div class="field">
        <label>Se cobra</label>
        <select class="line-per">
          ${PER_OPTIONS.map(
            ([value, label]) =>
              `<option value="${value}"${(line.per ?? "fijo") === value ? " selected" : ""}>${label}</option>`
          ).join("")}
        </select>
      </div>
      ${
        isPrice
          ? `<div class="field line-included-field"${(line.per ?? "fijo") === "fijo" ? " hidden" : ""}>
              <label>A partir de</label>
              <input type="number" step="1" min="0" inputmode="numeric" class="line-included"
                     value="${line.included ?? 0}" title="Se cobra desde esta cantidad en adelante" />
            </div>`
          : where === "package"
            ? `<div class="field line-quote-field">
                <label>Presupuesto</label>
                <label class="checkbox-line">
                  <input type="checkbox" class="line-quote" ${quote ? "checked" : ""} />
                  <span>por evento</span>
                </label>
              </div>`
            : `<div class="field"></div>`
      }
      <div class="line-total">
        <span class="label">Total</span>
        <span class="value" data-line-total>—</span>
      </div>
      <button type="button" class="icon-btn danger remove-line" title="Quitar esta línea">✕</button>
    </div>`;
}

export function eventFormHtml({ products, packages = [], event = null, finance = null, includeFinance = false, coordinators = [] }) {
  // Un producto marcado como "sin uso" deja de ofrecerse, pero sigue apareciendo
  // si es el que ya tiene este evento: editar un evento viejo no puede vaciarle
  // el producto en silencio.
  const productOptions = products
    .filter((p) => p.active !== false || event?.product_id === p.id)
    .map(
      (p) =>
        `<option value="${escapeHtml(p.id)}"${event?.product_id === p.id ? " selected" : ""}>${escapeHtml(p.name)}</option>`
    )
    .join("");

  const costLines = Array.isArray(finance?.cost_lines) ? finance.cost_lines : [];
  const priceLines = Array.isArray(finance?.price_lines) ? finance.price_lines : [];

  return `
    <form id="event-form">
      <div class="form-grid">
        <div class="field">
          <label for="f-child_name">Nombre del cumpleañero</label>
          <input type="text" id="f-child_name" name="child_name" required
                 value="${escapeHtml(event?.child_name ?? "")}" />
        </div>
        <div class="field">
          <label for="f-contact">Teléfono de contacto</label>
          <input type="tel" inputmode="tel" id="f-contact" name="contact" required
                 value="${escapeHtml(event?.contact ?? "")}" />
        </div>
        <div class="field full">
          <label for="f-description">Descripción del evento</label>
          <input type="text" id="f-description" name="description" required value="${escapeHtml(event?.description ?? "")}" />
          <p class="hint">Se arma sola con el nombre y la edad. Se puede cambiar.</p>
        </div>
        <div class="field">
          <label for="f-product_id">Producto</label>
          <select id="f-product_id" name="product_id">
            <option value="">— Sin producto —</option>
            ${productOptions}
          </select>
        </div>
        <div class="field">
          <label for="f-package_id">Paquete</label>
          <select id="f-package_id" name="package_id">
            <option value="">— Sin paquete —</option>
          </select>
        </div>
        <div class="field">
          <label for="f-date">Fecha del cumpleaños</label>
          <input type="date" id="f-date" name="date" required value="${escapeHtml(event?.date ?? toLocalISODate())}" />
        </div>
        <div class="field">
          <label for="f-start_time">Hora</label>
          <div class="time-range">
            <input type="time" id="f-start_time" name="start_time" step="900"
                   value="${escapeHtml((event?.start_time ?? "").slice(0, 5))}" />
            <span>a</span>
            <input type="time" id="f-end_time" name="end_time" step="900"
                   value="${escapeHtml((event?.end_time ?? "").slice(0, 5))}" aria-label="Hora de fin" />
          </div>
        </div>
        ${
          // Sólo para quien vende. Es dato operativo (vive en events), pero al
          // empleado no le corresponde y le alargaría el formulario.
          includeFinance
            ? `<div class="field">
                <label for="f-sale_date">Fecha de venta</label>
                <input type="date" id="f-sale_date" name="sale_date"
                       value="${escapeHtml(event ? event.sale_date ?? "" : toLocalISODate())}" />
                <p class="hint">Vacío = no se sabe. Se usa para medir cuánta anticipación tienen las ventas.</p>
              </div>`
            : ""
        }
        ${OPERATIONAL_NUMBERS.map((f) => numberField(f, event)).join("")}
      </div>

      <!-- campos propios del producto elegido (products.form_schema) -->
      <div class="form-grid" id="product-fields"></div>

      <div class="label" style="margin-top:14px">Datos del cliente</div>
      <p class="hint" style="margin:2px 0 8px">No hace falta completarlos para guardar.</p>
      <div class="form-grid">
        <div class="field">
          <label for="f-child_age">Edad</label>
          <input type="number" step="1" min="0" max="21" inputmode="numeric" id="f-child_age" name="child_age"
                 value="${event?.child_age ?? ""}" />
        </div>
        <div class="field">
          <label for="f-guardian_name">Madre / padre</label>
          <input type="text" id="f-guardian_name" name="guardian_name" value="${escapeHtml(event?.guardian_name ?? "")}" />
        </div>
        <div class="field">
          <label for="f-guardian_contact">Su teléfono</label>
          <input type="tel" inputmode="tel" id="f-guardian_contact" name="guardian_contact"
                 value="${escapeHtml(event?.guardian_contact ?? "")}" />
        </div>
        <div class="field">
          <label for="f-email">Email</label>
          <input type="email" inputmode="email" id="f-email" name="email" value="${escapeHtml(event?.email ?? "")}" />
        </div>
        <div class="field">
          <label for="f-assigned_coordinator">Coordina</label>
          <input type="text" id="f-assigned_coordinator" name="assigned_coordinator" list="coordinator-list"
                 value="${escapeHtml(event?.assigned_coordinator ?? "")}" />
          <datalist id="coordinator-list">
            ${coordinators.map((c) => `<option value="${escapeHtml(c)}"></option>`).join("")}
          </datalist>
          <p class="hint">Sólo referencia. No le da acceso al sistema.</p>
        </div>
        <div class="field full">
          <label for="f-allergies">Alergias</label>
          <input type="text" id="f-allergies" name="allergies" placeholder="Ninguna conocida"
                 value="${escapeHtml(event?.allergies ?? "")}" />
          <p class="hint">Se muestra destacado en la ficha del evento.</p>
        </div>
      </div>

      ${
        includeFinance && !event
          ? `<div class="form-grid">
              <div class="field">
                <label for="f-repeat">Repetir</label>
                <select id="f-repeat" name="repeat">
                  <option value="">No repetir</option>
                  <option value="semanal">Cada semana</option>
                  <option value="quincenal">Cada 15 días</option>
                  <option value="mensual">Cada mes</option>
                </select>
              </div>
              <div class="field" id="repeat-until-field" hidden>
                <label for="f-repeat-until">Repetir hasta</label>
                <input type="date" id="f-repeat-until" name="repeat_until" />
              </div>
              <p class="hint" id="repeat-preview" style="grid-column: 1 / -1"></p>
            </div>`
          : ""
      }

      <div class="field full">
        <label for="f-notes">Notas</label>
        <textarea id="f-notes" name="notes" rows="2">${escapeHtml(event?.notes ?? "")}</textarea>
      </div>

      ${
        includeFinance
          ? `<div class="finance-section">
              <h4 class="section-title">Números del evento <span class="admin-only-tag">sólo la familia</span></h4>

              <div class="label">Costos</div>
              <div id="cost-lines">
                ${costLines.map((l) => costLineRowHtml(l, "cost")).join("")}
              </div>
              <p class="hint" id="no-lines-hint"${costLines.length ? " hidden" : ""}>
                Este evento no tiene costos cargados. Elegí un paquete y se traen solos,
                o agregá las líneas a mano.
              </p>
              <p class="quote-warning" id="quote-warning" hidden>
                Este paquete tiene un costo que se cotiza en cada cumpleaños. Cargá el monto
                que te pasaron en la línea marcada.
              </p>
              <button type="button" class="icon-btn" id="add-cost-line">+ Agregar costo</button>

              <div class="label" style="margin-top:18px">Precio</div>
              <p class="hint" style="margin:2px 0 8px">
                Se calcula solo con los chicos y profesores reales. Las líneas "a partir de"
                sólo cuentan lo que pase de esa cantidad.
              </p>
              <div id="price-lines">
                ${priceLines.map((l) => costLineRowHtml(l, "price")).join("")}
              </div>
              <button type="button" class="icon-btn" id="add-price-line">+ Agregar línea de precio</button>
              <p class="hint" id="price-total-hint" style="margin-top:8px"></p>

              <div class="label" style="margin-top:14px">Cobranza</div>
              <p class="hint" style="margin:2px 0 8px">
                La seña entra al vender; el saldo se cobra el día del cumple. Sólo hay
                que marcar si no pasó.
              </p>
              <div class="form-grid">
                ${numberField(["deposit", "Seña cobrada", "any"], finance)}
                <div class="field" id="balance-pending-field" hidden>
                  <label for="f-balance_pending">Saldo</label>
                  <label class="checkbox-line">
                    <input type="checkbox" id="f-balance_pending" name="balance_pending"
                           ${finance?.balance_pending ? "checked" : ""} />
                    <span>Todavía debe el saldo</span>
                  </label>
                  <p class="hint">Marcalo sólo si el cumple ya pasó y no pagó.</p>
                </div>
              </div>
              <p class="hint" id="balance-due-hint"></p>

              <div class="summary-box">
                <div class="item">
                  <div class="label">Costos totales</div>
                  <div class="value" id="summary-cost">—</div>
                </div>
                <div class="item">
                  <div class="label">Ganancia</div>
                  <div class="value" id="summary-profit">—</div>
                </div>
                <div class="item">
                  <div class="label">Margen</div>
                  <div class="value" id="summary-margin">—</div>
                </div>
              </div>
              <p class="margin-warning" id="margin-warning" hidden></p>
            </div>`
          : ""
      }

      <p class="error-text" id="event-form-error" hidden></p>

      <div class="modal-actions">
        <button type="button" class="btn secondary" id="event-cancel">Cancelar</button>
        <button type="submit" class="btn" id="event-submit">Guardar evento</button>
      </div>
    </form>
  `;
}

/** Dibuja los campos declarados en products.form_schema del producto elegido. */
function productFieldsHtml(product, details = {}) {
  const schema = Array.isArray(product?.form_schema) ? product.form_schema : [];
  if (schema.length === 0) return "";

  return schema
    .map((field) => {
      const value = details?.[field.name] ?? "";
      const id = `d-${field.name}`;

      if (field.type === "select") {
        const options = (field.options ?? [])
          .map((o) => `<option value="${escapeHtml(o)}"${value === o ? " selected" : ""}>${escapeHtml(o)}</option>`)
          .join("");
        return `<div class="field">
          <label for="${id}">${escapeHtml(field.label)}</label>
          <select id="${id}" data-detail="${escapeHtml(field.name)}"><option value="">—</option>${options}</select>
        </div>`;
      }

      if (field.type === "checkbox") {
        return `<div class="field checkbox-field">
          <label for="${id}">
            <input type="checkbox" id="${id}" data-detail="${escapeHtml(field.name)}"${value ? " checked" : ""} />
            ${escapeHtml(field.label)}
          </label>
        </div>`;
      }

      const type = field.type === "number" ? "number" : "text";
      return `<div class="field">
        <label for="${id}">${escapeHtml(field.label)}</label>
        <input type="${type}"${type === "number" ? ' inputmode="decimal" step="any"' : ""}
               id="${id}" data-detail="${escapeHtml(field.name)}" value="${escapeHtml(value)}" />
      </div>`;
    })
    .join("");
}

/** Parte operativa: lo que va a la tabla events. Nunca incluye plata. */
export function readOperational(form) {
  const details = {};
  form.querySelectorAll("[data-detail]").forEach((el) => {
    details[el.dataset.detail] = el.type === "checkbox" ? el.checked : el.value;
  });

  const payload = {
    description: form.description.value.trim(),
    product_id: form.product_id.value || null,
    package_id: form.package_id.value || null,
    date: form.date.value,
    notes: form.notes.value.trim() || null,
    details,
  };

  // Sólo si el campo está dibujado: si no, editar desde un formulario que no
  // lo muestra borraría la fecha de venta que cargó la familia.
  if (form.sale_date) payload.sale_date = form.sale_date.value || null;
  payload.start_time = form.start_time?.value || null;
  payload.end_time = form.end_time?.value || null;

  for (const name of ["child_name", "contact", "guardian_name", "guardian_contact", "allergies", "email", "assigned_coordinator"]) {
    if (form[name]) payload[name] = form[name].value.trim() || null;
  }
  if (form.child_age) payload.child_age = form.child_age.value === "" ? null : Number(form.child_age.value);

  for (const [name] of OPERATIONAL_NUMBERS) {
    payload[name] = Number(form[name].value) || 0;
  }

  return payload;
}

/**
 * Lee las líneas dibujadas dentro de `root`, en orden. Cada tipo guarda su
 * extra: las de costo `quote` (a presupuestar), las de precio `included`
 * (a partir de qué cantidad). Una línea "a presupuestar" se conserva aunque
 * el monto siga en cero: es justamente lo que falta cargar.
 */
export function readCostLines(root) {
  return [...root.querySelectorAll("[data-cost-line]")]
    .map((row) => {
      const line = {
        concept: row.querySelector(".line-concept").value.trim(),
        amount: parseAmount(row.querySelector(".line-amount").value),
        per: row.querySelector(".line-per").value,
      };
      const quoteBox = row.querySelector(".line-quote");
      if (quoteBox ? quoteBox.checked : row.dataset.quote === "1") line.quote = true;
      const included = row.querySelector(".line-included");
      if (included && Number(included.value) > 0 && line.per !== "fijo") line.included = Number(included.value);
      return line;
    })
    .filter((l) => l.concept || l.amount || l.quote);
}

/** Parte financiera: lo que va a event_finance. Sólo existe para el admin. */
export function readFinancial(form) {
  const costWrap = form.querySelector("#cost-lines");
  if (!costWrap) return null;
  return {
    deposit: parseAmount(form.deposit.value),
    balance_pending: Boolean(form.balance_pending?.checked),
    cost_lines: readCostLines(costWrap),
    price_lines: readCostLines(form.querySelector("#price-lines")),
  };
}

export function wireEventForm(root, {
  products,
  packages = [],
  packageRates = [],
  event = null,
  includeFinance = false,
  onSubmit,
  onCancel,
}) {
  const form = root.querySelector("#event-form");
  const errorEl = root.querySelector("#event-form-error");
  const submit = root.querySelector("#event-submit");
  const productSelect = form.product_id;
  const packageSelect = form.package_id;
  const productFields = root.querySelector("#product-fields");
  const linesWrap = root.querySelector("#cost-lines");
  const priceWrap = root.querySelector("#price-lines");
  const noLinesHint = root.querySelector("#no-lines-hint");

  // ---- campos propios del producto + paquetes filtrados ----
  const drawProductDependentFields = (keepDetails = false) => {
    const product = products.find((p) => p.id === productSelect.value) ?? null;
    const details = keepDetails ? readOperational(form).details : event?.details ?? {};

    productFields.innerHTML = productFieldsHtml(product, details);

    const current = packageSelect.value || event?.package_id || "";
    const forProduct = packages.filter((p) => p.product_id === productSelect.value);
    packageSelect.innerHTML =
      `<option value="">— Sin paquete —</option>` +
      forProduct
        .map(
          (p) => `<option value="${escapeHtml(p.id)}"${current === p.id ? " selected" : ""}>${escapeHtml(p.name)}</option>`
        )
        .join("");
    packageSelect.closest(".field").hidden = forProduct.length === 0;
  };

  productSelect.addEventListener("change", () => {
    drawProductDependentFields(true);
    refreshSummary();
  });

  // ---- al elegir paquete, traer sus líneas de costo (sólo admin) ----
  packageSelect.addEventListener("change", () => {
    if (!includeFinance) return;
    const rates = packageRates.find((r) => r.package_id === packageSelect.value);
    if (!rates) return;

    const lines = Array.isArray(rates.cost_lines) ? rates.cost_lines : [];
    const prices = Array.isArray(rates.price_lines) ? rates.price_lines : [];
    linesWrap.innerHTML = lines.map((l) => costLineRowHtml(l, "cost")).join("");
    priceWrap.innerHTML = prices.map((l) => costLineRowHtml(l, "price")).join("");
    formatAmountsIn(linesWrap);
    formatAmountsIn(priceWrap);
    if (rates.default_teachers_count && form.teachers_count) {
      form.teachers_count.value = rates.default_teachers_count;
    }
    refreshSummary();
  });

  // ---- resumen en vivo + aviso de margen bajo ----
  const costEl = root.querySelector("#summary-cost");
  const profitEl = root.querySelector("#summary-profit");
  const marginEl = root.querySelector("#summary-margin");
  const warningEl = root.querySelector("#margin-warning");

  function currentDraft() {
    return { ...readOperational(form), ...(readFinancial(form) ?? {}) };
  }

  function refreshSummary() {
    if (!includeFinance || !costEl) return;

    const draft = currentDraft();
    const cost = linesTotal(draft.cost_lines, draft);
    const income = eventIncome(draft);
    const profit = income - cost;
    const ratio = income > 0 ? profit / income : null;

    // Subtotal de cada línea, leyendo la fila directamente: así una fila
    // vacía o a presupuestar muestra "—" en vez de desalinear las demás.
    const paintTotals = (wrap) => {
      for (const row of wrap.querySelectorAll("[data-cost-line]")) {
        const amount = parseAmount(row.querySelector(".line-amount").value);
        const per = row.querySelector(".line-per").value;
        const included = Number(row.querySelector(".line-included")?.value) || 0;
        const quoteBox = row.querySelector(".line-quote");
        const quote = quoteBox ? quoteBox.checked : row.dataset.quote === "1";
        const el = row.querySelector("[data-line-total]");
        row.classList.toggle("needs-quote", Boolean(quote) && !(amount > 0));
        const incField = row.querySelector(".line-included-field");
        if (incField) incField.hidden = per === "fijo";
        el.textContent = amount > 0 ? formatCurrency(lineTotal({ amount, per, included }, draft)) : "—";
      }
    };
    paintTotals(linesWrap);
    paintTotals(priceWrap);

    if (noLinesHint) noLinesHint.hidden = linesWrap.children.length > 0;
    const quoteWarn = root.querySelector("#quote-warning");
    if (quoteWarn) quoteWarn.hidden = !needsQuote(draft.cost_lines);
    const priceHint = root.querySelector("#price-total-hint");
    if (priceHint) priceHint.textContent = `Precio del evento: ${formatCurrency(income)}`;

    // El saldo no se guarda: es precio - seña, y se muestra para no hacer la
    // cuenta a mano.
    const dueEl = root.querySelector("#balance-due-hint");
    if (dueEl) {
      const c = collectionOf({ ...draft, balance_pending: form.balance_pending?.checked }, toLocalISODate());
      const [label, hint] = COLLECTION_STATES[c.state];
      dueEl.textContent = `Saldo ${formatCurrency(c.balance)} · ${label}: ${hint}`;
      // La excepción sólo tiene sentido si el cumple ya pasó.
      const pendingField = root.querySelector("#balance-pending-field");
      if (pendingField) pendingField.hidden = !c.happened;
    }

    costEl.textContent = formatCurrency(cost);
    profitEl.textContent = formatCurrency(profit);
    profitEl.className = `value ${profit >= 0 ? "positive" : "negative"}`;
    marginEl.textContent = ratio === null ? "—" : `${Math.round(ratio * 100)}%`;
    marginEl.className = `value ${ratio !== null && ratio < LOW_MARGIN_RATIO ? "negative" : ""}`;

    if (income > 0 && ratio < LOW_MARGIN_RATIO) {
      warningEl.textContent =
        profit < 0
          ? `Ojo: así como está, este evento pierde ${formatCurrency(Math.abs(profit))}.`
          : `Ojo: el margen queda en ${Math.round(ratio * 100)}%, por debajo del ${Math.round(LOW_MARGIN_RATIO * 100)}% habitual.`;
      warningEl.hidden = false;
    } else {
      warningEl.hidden = true;
    }
  }

  // ---- la descripción se arma sola con el nombre y la edad ----
  //
  // Sólo se pisa mientras siga siendo la que generamos nosotros (o esté
  // vacía). Apenas alguien la edita a mano, deja de tocarse: escribir el
  // nombre dos veces es molesto, pero perder lo que uno escribió es peor.
  const derivedDescription = () => {
    const name = form.child_name.value.trim();
    if (!name) return "";
    const age = form.child_age?.value;
    return age ? `Cumple de ${name}, ${age} años` : `Cumple de ${name}`;
  };

  let lastDerived = event ? null : derivedDescription();

  const refreshDescription = () => {
    const current = form.description.value.trim();
    if (current !== "" && current !== lastDerived) return; // editada a mano
    const next = derivedDescription();
    if (!next) return;
    form.description.value = next;
    lastDerived = next;
  };

  form.child_name.addEventListener("input", refreshDescription);
  form.child_age?.addEventListener("input", refreshDescription);

  form.addEventListener("input", refreshSummary);
  form.addEventListener("change", refreshSummary);

  if (includeFinance) {
    root.querySelector("#add-cost-line").addEventListener("click", () => {
      linesWrap.insertAdjacentHTML("beforeend", costLineRowHtml(undefined, "cost"));
      formatAmountsIn(linesWrap);
      refreshSummary();
    });
    root.querySelector("#add-price-line").addEventListener("click", () => {
      priceWrap.insertAdjacentHTML("beforeend", costLineRowHtml(undefined, "price"));
      formatAmountsIn(priceWrap);
      refreshSummary();
    });

    for (const wrap of [linesWrap, priceWrap]) {
      wrap.addEventListener("click", (e) => {
        if (e.target.classList.contains("remove-line")) {
          e.target.closest("[data-cost-line]").remove();
          refreshSummary();
        }
      });
    }
  }

  // ---- repetición ----
  const repeatSelect = form.repeat;
  const untilField = root.querySelector("#repeat-until-field");
  const previewEl = root.querySelector("#repeat-preview");

  const refreshRepeat = () => {
    if (!repeatSelect) return;
    const on = Boolean(repeatSelect.value);
    untilField.hidden = !on;
    if (!on) {
      previewEl.textContent = "";
      return;
    }
    const dates = repeatDates(form.date.value, repeatSelect.value, form.repeat_until.value);
    previewEl.textContent =
      dates.length > 0
        ? `Se van a crear ${dates.length} evento(s), del ${dates[0]} al ${dates[dates.length - 1]}.`
        : "Elegí hasta cuándo repetir.";
  };

  repeatSelect?.addEventListener("change", refreshRepeat);
  form.repeat_until?.addEventListener("change", refreshRepeat);
  form.date.addEventListener("change", refreshRepeat);
  refreshRepeat();

  root.querySelector("#event-cancel").addEventListener("click", () => onCancel?.());

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    errorEl.hidden = true;

    // Aviso ANTES de guardar, no después en un reporte.
    if (includeFinance) {
      const draft = currentDraft();
      const income = eventIncome(draft);
      const profit = eventProfit(draft);
      const ratio = income > 0 ? profit / income : null;

      if (ratio !== null && ratio < LOW_MARGIN_RATIO) {
        const message =
          profit < 0
            ? `Este evento pierde ${formatCurrency(Math.abs(profit))}. ¿Lo guardás igual?`
            : `El margen es de sólo ${Math.round(ratio * 100)}% (${formatCurrency(profit)}). ¿Lo guardás igual?`;
        if (!window.confirm(message)) return;
      }
    }

    submit.disabled = true;
    submit.textContent = "Guardando…";
    try {
      const repeat = repeatSelect?.value
        ? {
            frequency: repeatSelect.value,
            until: form.repeat_until.value,
            dates: repeatDates(form.date.value, repeatSelect.value, form.repeat_until.value),
          }
        : null;

      await onSubmit({ operational: readOperational(form), financial: readFinancial(form), repeat });
    } catch (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
      submit.disabled = false;
      submit.textContent = "Guardar evento";
    }
  });

  wireAmountInputs(root);
  drawProductDependentFields(false);
  refreshSummary();
}

/**
 * Fechas de una serie repetida, desde `start` hasta `until` inclusive.
 * Los cumpleaños suelen repetirse semanalmente; la colonia va por temporada.
 */
export function repeatDates(start, frequency, until) {
  if (!start || !until || !frequency) return [];

  const dates = [];
  const cursor = new Date(`${start}T00:00:00`);
  const end = new Date(`${until}T00:00:00`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime()) || end < cursor) return [];

  // tope de seguridad: nadie quiere crear 500 eventos por un error de fecha
  while (cursor <= end && dates.length < 200) {
    dates.push(toLocalISODate(cursor));
    if (frequency === "semanal") cursor.setDate(cursor.getDate() + 7);
    else if (frequency === "quincenal") cursor.setDate(cursor.getDate() + 14);
    else cursor.setMonth(cursor.getMonth() + 1);
  }

  return dates;
}
